import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { formatPrice, defaultVariant, cartKey, discountPct } from "../data/products.js";
import ProductImage from "./ProductImage.jsx";

export default function ProductCard({ product }) {
  const { items, add, remove } = useCart();
  const variants = product.variants || null;
  const [sizeId, setSizeId] = useState(() => defaultVariant(product)?.id || null);

  const variant = variants ? variants.find((v) => v.id === sizeId) || variants[0] : null;
  const key = cartKey(product.id, variant?.id);
  const qty = items[key] || 0;
  const price = variant?.price ?? product.pricePerKg;
  const unit = variant?.unit || product.unit;
  const mrp = variant?.mrp ?? product.mrp;
  const off = discountPct(price, mrp);
  const soldOut = Boolean(product.soldOut);

  // Total across every size of this product, so switching to an unadded size
  // doesn't make it look like the bag forgot the other one.
  const totalInBag = variants
    ? variants.reduce((n, v) => n + (items[cartKey(product.id, v.id)] || 0), 0)
    : qty;

  return (
    <article className="pc">
      {/* No badge chip over the packshot — the label already carries the
          product's claims, and the tag line below repeats them. */}
      {/* Image and name open the product's own page with full specifications;
          the size chips and ADD/stepper below stay quick-add controls. */}
      <Link
        to={`/products/${product.id}`}
        className={`pc__thumb ${soldOut ? "pc__thumb--out" : ""}`}
        aria-label={`${product.name} details`}
      >
        <ProductImage product={product} className="pc__img" override={variant?.image} />
        {/* Savings ribbon — hidden while sold out, where it would only tease. */}
        {off > 0 && !soldOut && <span className="pc__off">{off}% OFF</span>}
        {soldOut && <span className="pc__soldout">Out of stock</span>}
      </Link>
      <div className="pc__body">
        <p className="pc__tag">{product.tag}</p>
        <h3>
          <Link to={`/products/${product.id}`} className="pc__title-link">{product.name}</Link>
        </h3>

        {variants ? (
          <div className="pc__sizes" role="group" aria-label={`Pack size for ${product.name}`}>
            {variants.map((v) => {
              const inBag = items[cartKey(product.id, v.id)] || 0;
              return (
                <button
                  key={v.id}
                  type="button"
                  className={`pc__size ${v.id === variant.id ? "is-active" : ""}`}
                  onClick={() => setSizeId(v.id)}
                  aria-pressed={v.id === variant.id}
                >
                  {v.label}
                  {inBag > 0 && <span className="pc__size-dot" aria-label={`${inBag} in bag`} />}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="pc__unit">{product.unit}</p>
        )}

        {/* Price left, buy control right — the two things a shopper compares. */}
        <div className="pc__buy">
          <p className="pc__price">
            {formatPrice(price)}
            {mrp > price && <s className="pc__mrp">{formatPrice(mrp)}</s>}
            {variant && <span className="pc__per">/ {variant.label}</span>}
          </p>
          {soldOut ? (
            <span className="pc__notify">Back soon</span>
          ) : qty === 0 ? (
            <button className="pc__add" onClick={() => add(key)}>
              ADD
            </button>
          ) : (
            <div className="pc__stepper">
              <button onClick={() => remove(key)} aria-label={`Remove one ${unit}`}>
                −
              </button>
              <span>{qty}</span>
              <button onClick={() => add(key)} aria-label={`Add one ${unit}`}>
                +
              </button>
            </div>
          )}
        </div>

        {variants && totalInBag > qty && (
          <p className="pc__inbag">{totalInBag} packs of this oil in your bag</p>
        )}
      </div>
    </article>
  );
}
